import { isAuthenticated } from "./auth";

/** Bound `:name` segments from the matched path, empty for an exact route. */
export type RouteParams = Record<string, string>;

export type RouteRenderer = (
  root: HTMLElement,
  params: RouteParams,
) => void | Promise<void>;

interface Route {
  /** Exact path, or one containing `:name` segments — `/signals/:ref`. */
  path: string;
  render: RouteRenderer;
  publicRoute?: boolean; // skip auth gate
}

const routes: Route[] = [];

/** Cleanups to run before the next page renders, then forget.
 *
 * A list, not a single slot. It was a slot while it had one caller, and the
 * second caller would have silently replaced the first — leaving whichever page
 * registered earlier polling forever, which is the exact bug this exists to
 * prevent. Both callers start intervals: signal_management's status poll and
 * log stream, dashboard's /reports/today poll. */
const _pageCleanups: Array<() => void> = [];

/** Register a one-shot cleanup that runs before the next page is rendered. */
export function registerPageCleanup(fn: () => void): void {
  _pageCleanups.push(fn);
}

export function registerRoute(route: Route): void {
  routes.push(route);
}

export function navigate(path: string): void {
  if (location.pathname === path) {
    renderCurrent();
    return;
  }
  history.pushState({}, "", path);
  renderCurrent();
}

/**
 * The route for *path*, and whatever `:name` segments it binds.
 *
 * **Exact matches are tried first, and that ordering is the whole point.**
 * `/signals/manage` is a real route and `/signals/:ref` is a real route, and a
 * single pass in registration order would resolve one of them by accident —
 * working today and breaking silently the first time somebody reordered
 * main.ts. Specific beats general, always, regardless of registration order.
 */
function findRoute(path: string): { route: Route; params: RouteParams } | undefined {
  const exact = routes.find((r) => r.path === path);
  if (exact) return { route: exact, params: {} };

  const segments = path.split("/");
  for (const route of routes) {
    if (!route.path.includes(":")) continue;
    const routeSegments = route.path.split("/");
    if (routeSegments.length !== segments.length) continue;
    const params: RouteParams = {};
    let matched = true;
    for (let i = 0; i < routeSegments.length; i++) {
      const expected = routeSegments[i]!;
      const actual = segments[i]!;
      if (expected.startsWith(":")) {
        // An empty segment is not a value: /signals/ must not match
        // /signals/:ref and render a page with a blank ref.
        if (!actual) { matched = false; break; }
        params[expected.slice(1)] = decodeURIComponent(actual);
      } else if (expected !== actual) {
        matched = false;
        break;
      }
    }
    if (matched) return { route, params };
  }
  return undefined;
}

export function renderCurrent(): void {
  const root = document.getElementById("app");
  if (!root) return;

  const path = location.pathname;
  const match = findRoute(path);

  if (!match) {
    // Default: redirect to dashboard if logged in, otherwise login.
    if (path === "/") {
      navigate(isAuthenticated() ? "/dashboard" : "/login");
      return;
    }
    root.innerHTML = `<div class="placeholder-screen"><h2>Not Found</h2><p>${path}</p></div>`;
    return;
  }

  const { route, params } = match;

  if (!route.publicRoute && !isAuthenticated()) {
    navigate("/login");
    return;
  }

  if (route.publicRoute && isAuthenticated() && path === "/login") {
    navigate("/dashboard");
    return;
  }

  // Drained before any of them run, so a cleanup that throws cannot strand the
  // rest, and one that navigates cannot re-enter this list.
  const cleanups = _pageCleanups.splice(0);
  for (const cleanup of cleanups) {
    try {
      cleanup();
    } catch (err) {
      console.error("Page cleanup failed", err);
    }
  }
  root.innerHTML = "";
  void route.render(root, params);
}

export function initRouter(): void {
  window.addEventListener("popstate", renderCurrent);
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const link = target.closest<HTMLAnchorElement>("a[data-route]");
    if (!link) return;
    e.preventDefault();
    const href = link.getAttribute("href");
    if (href) navigate(href);
  });
  renderCurrent();
}
